/**
 * Titular en prosa de los Overview de ops y comercial.
 *
 * El del founder vive en el contrato (`titularFounder`), porque su insumo es
 * `AdminOverview` y lo comparten API y front. Estos dos no existen todavía en
 * `@ops/contracts` — es la carencia que anoté al entregar el módulo — así que
 * viven acá con la **misma forma** (`TitularFounder`) y la misma regla:
 *
 *  - todo número sale del payload que la pantalla ya muestra abajo, nada
 *    hardcodeado y nada que no se pueda verificar en la misma pantalla;
 *  - cuando un dato falta la oración lo dice, no lo inventa;
 *  - **ni una sola cifra de plata**: ops y comercial no reciben campos de $$ del
 *    servidor, y el titular tampoco los nombra.
 */
import type {
  Client,
  ComercialOverview,
  FunnelSummary,
  MetricValue,
  OpsOverview,
  TitularFounder,
} from '@ops/contracts'

import {
  formatMetricValue,
  formatPeriodo,
  formatRelativeDays,
  listaEs,
  plural,
} from '../../lib/format'
import type { DeudaDeDatos } from './deuda'

export type Titular = TitularFounder

// ---------------------------------------------------------------------------
// Ops — "¿qué tengo que mover hoy?"
// ---------------------------------------------------------------------------

export function titularOps(
  p: OpsOverview,
  deuda: DeudaDeDatos,
  now: Date,
): Titular {
  const cola = p.colaImplementacion.length
  const espera = diasMaximos(p.colaImplementacion)
  const riesgo = p.enRiesgo.length
  const brecha = brechaTotal(p.brechaCritica)

  const arriba =
    cola === 0
      ? 'La cola de implementación está vacía'
      : `La cola tiene ${cuenta(cola)} ${plural(cola, 'cuenta', 'cuentas')} esperando, la más vieja hace ${cuenta(espera)} días`

  const abajo =
    riesgo === 0
      ? 'y ninguna cuenta está en retención'
      : `y ${cuenta(riesgo)} ${plural(riesgo, 'cuenta está', 'cuentas están')} en retención`

  return {
    veredicto: `${arriba}, ${abajo}.`,
    loQueVaBien: loQueVaBienOps(p, cola, riesgo),
    loQueFrena: loQueFrenaOps(p, cola, espera, brecha, riesgo),
    loQueNoSeLee: loQueNoSeLee(deuda, now),
  }
}

function loQueVaBienOps(p: OpsOverview, cola: number, riesgo: number): string {
  const partes: string[] = []

  if (cola === 0) {
    partes.push('no hay ninguna cuenta firmada esperando implementación')
  }
  if (riesgo === 0) {
    partes.push('ninguna cuenta está en retención')
  }
  const verde = enVerde(p.heroMetrics)
  if (verde.length > 0) partes.push(cumplenBenchmark(verde))

  if (partes.length === 0) {
    return 'Todavía no hay ningún frente en verde con dato cargado: no hay nada que anotar como logro del mes.'
  }
  return oracion(partes)
}

function loQueFrenaOps(
  p: OpsOverview,
  cola: number,
  espera: number,
  brecha: number,
  riesgo: number,
): string {
  const partes: string[] = []

  if (cola > 0) {
    partes.push(
      `${cuenta(cola)} ${plural(cola, 'cuenta firmada sin implementar', 'cuentas firmadas sin implementar')}, con la más vieja esperando ${cuenta(espera)} días`,
    )
  }
  if (brecha > 0) {
    partes.push(
      `${cuenta(brecha)} ${plural(brecha, 'empleado digital contratado que no está instalado', 'empleados digitales contratados que no están instalados')} en las ${cuenta(p.brechaCritica.length)} cuentas de mayor brecha`,
    )
  }
  if (riesgo > 0) {
    partes.push(
      `${cuenta(riesgo)} ${plural(riesgo, 'cuenta en retención', 'cuentas en retención')}, la más antigua hace ${cuenta(diasMaximos(p.enRiesgo))} días`,
    )
  }
  const criticas = fueraDeUmbral(p.heroMetrics)
  if (criticas !== null) partes.push(criticas)

  if (partes.length === 0) {
    return 'Nada trabado: ni cuentas sin implementar ni empleados digitales sin instalar.'
  }
  return oracion(partes)
}

// ---------------------------------------------------------------------------
// Comercial — "¿cómo viene la máquina de adquisición?"
// ---------------------------------------------------------------------------

export function titularComercial(
  p: ComercialOverview,
  deuda: DeudaDeDatos,
  now: Date,
): Titular {
  const entrada = p.funnel.etapas[0]
  const cuello = cuelloDelFunnel(p.funnel)

  const arriba =
    entrada === undefined
      ? `El funnel de ${formatPeriodo(p.funnel.periodo)} no tiene etapas cargadas`
      : `El funnel de ${formatPeriodo(p.funnel.periodo)} entró con ${cuenta(entrada.cantidad)} en ${entrada.nombre.toLowerCase()}`

  const abajo =
    cuello === null
      ? 'y todavía ninguna etapa tiene conversión medida'
      : `y el cuello está de ${cuello.desde.toLowerCase()} a ${cuello.hasta.toLowerCase()}: pasa el ${pct(cuello.conversionPct)}`

  return {
    veredicto: `${arriba}, ${abajo}.`,
    loQueVaBien: loQueVaBienComercial(p),
    loQueFrena: loQueFrenaComercial(p, cuello),
    loQueNoSeLee: loQueNoSeLeeComercial(p, deuda, now),
  }
}

function loQueVaBienComercial(p: ComercialOverview): string {
  const partes: string[] = []

  const mejor = mejorConversion(p.funnel)
  if (mejor !== null) {
    partes.push(
      `de ${mejor.desde.toLowerCase()} a ${mejor.hasta.toLowerCase()} pasa el ${pct(mejor.conversionPct)}, la mejor conversión del funnel`,
    )
  }

  const verde = enVerde(p.heroMetrics)
  if (verde.length > 0) partes.push(cumplenBenchmark(verde))

  const expansion = p.oportunidadesExpansion.length
  if (expansion > 0) {
    partes.push(
      `${cuenta(expansion)} ${plural(expansion, 'cuenta está', 'cuentas están')} en expansión, listas para trabajar`,
    )
  }

  if (partes.length === 0) {
    return 'Todavía no hay ninguna etapa ni métrica en verde con dato cargado: no hay nada que anotar como logro del mes.'
  }
  return oracion(partes)
}

function loQueFrenaComercial(
  p: ComercialOverview,
  cuello: Conversion | null,
): string {
  const partes: string[] = []

  if (cuello !== null) {
    partes.push(
      `el cuello del funnel está de ${cuello.desde.toLowerCase()} a ${cuello.hasta.toLowerCase()}, donde pasa el ${pct(cuello.conversionPct)}`,
    )
  }

  const vacias = p.funnel.etapas.filter((e) => e.cantidad === 0)
  if (vacias.length > 0) {
    partes.push(
      `${plural(vacias.length, 'la etapa', 'las etapas')} ${listaEs(vacias.map((e) => e.nombre.toLowerCase()))} ${plural(vacias.length, 'cerró', 'cerraron')} el mes en cero`,
    )
  }

  const criticas = fueraDeUmbral(p.heroMetrics)
  if (criticas !== null) partes.push(criticas)

  if (partes.length === 0) {
    return 'Nada trabado: todas las etapas del funnel tienen movimiento y ninguna métrica está fuera de umbral.'
  }
  return oracion(partes)
}

function loQueNoSeLeeComercial(
  p: ComercialOverview,
  deuda: DeudaDeDatos,
  now: Date,
): string {
  const base = loQueNoSeLee(deuda, now)
  const sinLectura = canalesSinLectura(p.canales)

  if (sinLectura.length === 0) return base

  const frase = `${cuenta(sinLectura.length)} ${plural(sinLectura.length, 'canal no está activo y no tiene lectura de conversión', 'canales no están activos y no tienen lectura de conversión')}: ${listaEs(sinLectura.map((c) => c.nombre))}`

  // Cuando todo lo demás se lee, la deuda son solo los canales.
  return base.startsWith('Todas las métricas')
    ? oracion([frase])
    : `${base.slice(0, -1)}. ${mayuscula(frase)}.`
}

/** Un canal que no está activo no aporta lectura: es una decisión pendiente. */
export function canalesSinLectura(
  canales: ComercialOverview['canales'],
): ComercialOverview['canales'] {
  return canales.filter((c) => c.status.toLowerCase() !== 'activo')
}

// ---------------------------------------------------------------------------
// Piezas compartidas por los dos titulares
// ---------------------------------------------------------------------------

export interface Conversion {
  desde: string
  hasta: string
  conversionPct: number
}

/** Etapa con la peor conversión del funnel, con el nombre de la etapa previa. */
export function cuelloDelFunnel(funnel: FunnelSummary): Conversion | null {
  return extremoDeConversion(funnel, (candidata, actual) =>
    candidata.conversionPct < actual.conversionPct,
  )
}

export function mejorConversion(funnel: FunnelSummary): Conversion | null {
  return extremoDeConversion(funnel, (candidata, actual) =>
    candidata.conversionPct > actual.conversionPct,
  )
}

function extremoDeConversion(
  funnel: FunnelSummary,
  gana: (candidata: Conversion, actual: Conversion) => boolean,
): Conversion | null {
  let elegida: Conversion | null = null

  for (let i = 1; i < funnel.etapas.length; i += 1) {
    const etapa = funnel.etapas[i]
    const previa = funnel.etapas[i - 1]
    if (etapa === undefined || previa === undefined) continue
    if (etapa.conversionPct === null) continue

    const candidata: Conversion = {
      desde: previa.nombre,
      hasta: etapa.nombre,
      conversionPct: etapa.conversionPct,
    }
    if (elegida === null || gana(candidata, elegida)) elegida = candidata
  }

  return elegida
}

/** Métricas con dato y en target. */
export function enVerde(metricas: readonly MetricValue[]): MetricValue[] {
  return metricas.filter((m) => m.valor !== null && m.health === 'ok')
}

function cumplenBenchmark(verde: readonly MetricValue[]): string {
  const lista = listaEs(verde.map((m) => `${m.nombre} ${valor(m)}`))
  return `${lista} ${plural(verde.length, 'cumple', 'cumplen')} su benchmark`
}

function fueraDeUmbral(metricas: readonly MetricValue[]): string | null {
  const criticas = metricas.filter(
    (m) => m.valor !== null && m.health === 'critical',
  )
  if (criticas.length === 0) return null

  const lista = listaEs(
    criticas.map((m) =>
      m.benchmark === null
        ? `${m.nombre} en ${valor(m)}`
        : `${m.nombre} en ${valor(m)} contra un benchmark de ${m.benchmark}`,
    ),
  )
  return `fuera de umbral: ${lista}`
}

/**
 * "Lo que no se lee" a partir de la **misma deuda que dibuja el bloque**.
 *
 * Recibe la `DeudaDeDatos` ya derivada y no una lista de métricas: es la única
 * forma de que la oración y la tarjeta no puedan discrepar. Cuando esto miraba
 * `heroMetrics` por su cuenta, la prosa decía «Todas las métricas del período
 * tienen dato y carga vigente» con siete alertas de deuda en el mismo payload.
 *
 * Se exporta porque el titular del founder vive en `@ops/contracts` y calcula su
 * `loQueNoSeLee` sobre `heroMetrics`: `AdminOverview` lo pisa con esto.
 */
export function loQueNoSeLee(deuda: DeudaDeDatos, now: Date): string {
  const partes: string[] = []

  const faltan = deuda.sinDato
  if (faltan.length > 0) {
    const lista = listaEs(faltan.map((i) => i.nombre))
    partes.push(
      faltan.length === 1
        ? `Hay una métrica que hoy no podemos leer: ${lista}`
        : `Hay ${cuenta(faltan.length)} métricas que hoy no podemos leer: ${lista}`,
    )
  }

  const viejas = deuda.vencidas
  if (viejas.length > 0) {
    const lista = listaEs(
      viejas.map((i) =>
        // Sin `MetricValue` no sabemos de cuándo es la carga y no se inventa
        // una antigüedad: se nombra la métrica y nada más.
        i.metric === null
          ? i.nombre
          : `${i.nombre} (${formatRelativeDays(i.metric.provenance.updatedAt, now)})`,
      ),
    )
    partes.push(
      viejas.length === 1
        ? `la carga de ${lista} está vencida`
        : `las cargas de ${lista} están vencidas`,
    )
  }

  if (partes.length === 0) {
    return 'Todas las métricas del período tienen dato y carga vigente.'
  }
  return oracion(partes)
}

/** Días en estado de la cuenta más antigua de una lista. 0 si la lista está vacía. */
export function diasMaximos(clientes: readonly Client[]): number {
  return clientes.reduce((max, c) => Math.max(max, c.diasEnEstado), 0)
}

/** Empleados digitales contratados y no instalados en una lista de cuentas. */
export function brechaTotal(clientes: readonly Client[]): number {
  return clientes.reduce((total, c) => total + (c.activacion?.brecha ?? 0), 0)
}

function valor(m: MetricValue): string {
  return formatMetricValue(m.valor, m.unidad)
}

function pct(n: number): string {
  return formatMetricValue(n, 'percent')
}

function cuenta(n: number): string {
  return formatMetricValue(n, 'count')
}

function oracion(partes: readonly string[]): string {
  return `${partes.map(mayuscula).join('. ')}.`
}

function mayuscula(texto: string): string {
  return `${texto.charAt(0).toUpperCase()}${texto.slice(1)}`
}
